import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabaseClient';

const RANKING_BASE = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
const RANKING_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${RANKING_BASE}/ranking`,
  url: `${RANKING_BASE}/ranking`,
  name: 'Ranking de Tipsters Verificados por ROI | Apuestamente',
  description: 'Ranking de los mejores tipsters de apuestas deportivas en España, ordenados por ROI verificado. Fútbol, tenis, baloncesto y multi-sport.',
  inLanguage: 'es-ES',
  isPartOf: { '@id': `${RANKING_BASE}/#website` },
};

interface Tipster {
  id: string;
  username: string;
  avatar_url: string;
  roi: number;
  total_picks: number;
  won_picks: number;
  sport: string;
  speciality: string;
  is_verified: boolean;
  followers: number;
  avg_odds: number;
  ytd_profit: string;
  badge: string;
  badge_color: string;
  rank?: number;
}

function SkeletonRow() {
  return (
    <tr className="border-b border-[#1e2028]">
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className="px-5 py-4">
          <div className="h-4 bg-[#1e2028] rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );
}

export default function RankingPage() {
  const [tipsters, setTipsters] = useState<Tipster[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('Todos');
  const [sortBy, setSortBy] = useState('roi');

  const sports = ['Todos', 'Fútbol', 'Tenis', 'Baloncesto', 'Multi-sport'];

  useEffect(() => {
    document.title = 'Ranking de Tipsters Verificados por ROI | Apuestamente';
    const id = 'schema-ranking';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(RANKING_SCHEMA);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  useEffect(() => {
    const fetchTipsters = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, avatar_url, roi, total_picks, won_picks, sport, speciality, is_verified, followers, avg_odds, ytd_profit, badge, badge_color')
        .order('roi', { ascending: false });

      if (!error && data) {
        setTipsters(data as Tipster[]);
      }
      setLoading(false);
    };

    fetchTipsters();

    // Real-time: refresh when profiles change
    const channel = supabase
      .channel('profiles-ranking')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        fetchTipsters();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const filteredTipsters = useMemo(() => {
    let result = tipsters.filter((t) => activeSport === 'Todos' || t.sport === activeSport);

    if (sortBy === 'roi')     result = [...result].sort((a, b) => b.roi - a.roi);
    if (sortBy === 'picks')   result = [...result].sort((a, b) => b.total_picks - a.total_picks);
    if (sortBy === 'hitrate') {
      result = [...result].sort((a, b) => {
        const hrA = a.total_picks > 0 ? (a.won_picks / a.total_picks) * 100 : 0;
        const hrB = b.total_picks > 0 ? (b.won_picks / b.total_picks) * 100 : 0;
        return hrB - hrA;
      });
    }

    return result.map((t, idx) => ({ ...t, rank: idx + 1 }));
  }, [tipsters, activeSport, sortBy]);

  const podium = filteredTipsters.slice(0, 3);

  const getHitRate = (t: Tipster) =>
    t.total_picks > 0 ? ((t.won_picks / t.total_picks) * 100).toFixed(1) : '0.0';

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16">
        {/* Header */}
        <div className="relative bg-[#151719] border-b border-[#2a2d3a] overflow-hidden">
          <div
            className="absolute inset-0 opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, #ffd700 0%, transparent 60%)' }}
          ></div>
          <div className="relative max-w-[1440px] mx-auto px-6 py-10">
            <p className="text-[#ffd700] text-xs font-bold tracking-widest uppercase mb-2">
              Verificado por Resultados Reales
            </p>
            <h1 className="text-white text-4xl font-black uppercase mb-2">Ranking de Tipsters</h1>
            <p className="text-[#a8b2c1] text-sm">
              ROI calculado automáticamente · {tipsters.length} usuarios registrados
            </p>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-8">
            <div className="flex flex-col gap-1">
              <span className="text-[#6b7280] text-xs uppercase tracking-widest px-1">Deporte</span>
              <div className="flex items-center gap-1 bg-[#151719] border border-[#2a2d3a] rounded-xl p-1">
                {sports.map((s) => (
                  <button
                    key={s}
                    onClick={() => setActiveSport(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeSport === s
                        ? 'bg-[#ffd700]/15 text-[#ffd700] font-bold'
                        : 'text-[#6b7280] hover:text-white'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-end gap-2 pb-1">
              {activeSport !== 'Todos' && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ffd700]/10 border border-[#ffd700]/30 text-[#ffd700] text-xs font-bold">
                  <i className="ri-filter-3-line text-xs"></i>
                  {activeSport}
                  <button onClick={() => setActiveSport('Todos')} className="cursor-pointer hover:text-white">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              )}
              {!loading && (
                <span className="text-[#6b7280] text-xs self-center">
                  {filteredTipsters.length} tipster{filteredTipsters.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Empty state */}
          {!loading && filteredTipsters.length === 0 && (
            <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-16 text-center mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#ffd700]/10 mx-auto mb-4">
                <i className="ri-trophy-line text-[#ffd700] text-3xl"></i>
              </div>
              <p className="text-white font-bold text-lg">El ranking está vacío por ahora</p>
              <p className="text-[#6b7280] text-sm mt-2 max-w-sm mx-auto">
                {activeSport !== 'Todos'
                  ? `No hay tipsters de ${activeSport} todavía. Prueba con "Todos".`
                  : 'Cuando los usuarios registren picks y resultados, el ranking se actualizará aquí en tiempo real.'}
              </p>
              {activeSport !== 'Todos' && (
                <button
                  onClick={() => setActiveSport('Todos')}
                  className="mt-4 px-5 py-2.5 bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] text-sm font-bold rounded-xl cursor-pointer hover:bg-[#ffd700]/20 transition-all whitespace-nowrap"
                >
                  Ver todos los deportes
                </button>
              )}
            </div>
          )}

          {/* Podium — only when 3+ users */}
          {!loading && podium.length >= 3 && (
            <div className="grid grid-cols-3 gap-4 mb-10 max-w-3xl mx-auto">
              {[podium[1], podium[0], podium[2]].map((t, i) => {
                const positions = [2, 1, 3];
                const pos = positions[i];
                const sizes = ['h-48', 'h-56', 'h-44'];
                const colors = ['#a8b2c1', '#ffd700', '#ff6b35'];
                return (
                  <div key={t.id} className={`${i === 1 ? 'order-1 md:-mt-4' : ''} flex flex-col items-center`}>
                    <div
                      className={`w-full bg-[#151719] border rounded-2xl flex flex-col items-center justify-end pb-5 px-3 ${sizes[i]} ${
                        pos === 1 ? 'border-[#ffd700]/40' : 'border-[#2a2d3a]'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <div
                          className="w-12 h-12 flex items-center justify-center rounded-full mb-1 text-2xl font-black"
                          style={{ background: `${colors[i]}20`, color: colors[i] }}
                        >
                          #{pos}
                        </div>
                        {t.avatar_url ? (
                          <img
                            src={t.avatar_url}
                            alt={t.username}
                            className="w-12 h-12 rounded-full object-cover border-2 mb-2"
                            style={{ borderColor: colors[i] }}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center border-2 mb-2 text-lg font-black"
                            style={{ borderColor: colors[i], background: `${colors[i]}20`, color: colors[i] }}
                          >
                            {t.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <p className="text-white font-bold text-sm text-center truncate max-w-full">
                          {t.username}
                        </p>
                        <p className="text-xl font-black mt-1" style={{ color: colors[i] }}>
                          +{t.roi}%
                        </p>
                        <p className="text-[#6b7280] text-xs">ROI</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full ranking table */}
          {(loading || filteredTipsters.length > 0) && (
            <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-[#2a2d3a] flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-white font-bold">
                    Clasificación
                    {activeSport !== 'Todos' && (
                      <span className="text-[#ffd700] ml-1">· {activeSport}</span>
                    )}
                  </h3>
                  <p className="text-[#6b7280] text-xs mt-0.5">
                    Datos reales · Actualización en tiempo real
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#6b7280] text-xs">Ordenar por:</span>
                  {[
                    { id: 'roi', label: 'ROI' },
                    { id: 'picks', label: 'Picks' },
                    { id: 'hitrate', label: 'Aciertos' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSortBy(s.id)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all ${
                        sortBy === s.id ? 'bg-[#00e87a]/15 text-[#00e87a]' : 'text-[#6b7280] hover:text-white'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-[#1e2028]">
                      <th className="px-5 py-3 text-left text-[#6b7280] text-xs uppercase tracking-wide">#</th>
                      <th className="px-5 py-3 text-left text-[#6b7280] text-xs uppercase tracking-wide">Tipster</th>
                      <th className="px-4 py-3 text-center text-[#6b7280] text-xs uppercase">Deporte</th>
                      <th className="px-4 py-3 text-center text-[#6b7280] text-xs uppercase">ROI</th>
                      <th className="px-4 py-3 text-center text-[#6b7280] text-xs uppercase">Aciertos</th>
                      <th className="px-4 py-3 text-center text-[#6b7280] text-xs uppercase">Picks</th>
                      <th className="px-4 py-3 text-center text-[#6b7280] text-xs uppercase">Beneficio</th>
                      <th className="px-4 py-3 text-center text-[#6b7280] text-xs uppercase">Cuota media</th>
                      <th className="px-4 py-3 text-center text-[#6b7280] text-xs uppercase">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                      : filteredTipsters.map((t) => (
                          <tr
                            key={t.id}
                            className="border-b border-[#1e2028] hover:bg-white/[0.02] transition-colors cursor-pointer"
                          >
                            <td className="px-5 py-4">
                              <span
                                className={`font-black text-sm ${
                                  t.rank === 1
                                    ? 'text-[#ffd700]'
                                    : t.rank === 2
                                    ? 'text-white'
                                    : t.rank === 3
                                    ? 'text-[#ff6b35]'
                                    : 'text-[#6b7280]'
                                }`}
                              >
                                {t.rank}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <div className="relative flex-shrink-0">
                                  {t.avatar_url ? (
                                    <img
                                      src={t.avatar_url}
                                      alt={t.username}
                                      className="w-9 h-9 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-9 h-9 rounded-full bg-[#2a2d3a] flex items-center justify-center text-white font-bold text-sm">
                                      {t.username.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  {t.is_verified && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 flex items-center justify-center bg-[#00e87a] rounded-full">
                                      <i className="ri-check-line text-black" style={{ fontSize: '9px' }}></i>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-white text-sm font-semibold">{t.username}</p>
                                  <p className="text-[#6b7280] text-xs">
                                    {t.speciality || t.sport}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className="text-[#a8b2c1] text-xs">{t.sport || '—'}</span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span className="text-[#00e87a] font-black text-sm">
                                {t.roi > 0 ? '+' : ''}{t.roi}%
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  parseFloat(getHitRate(t)) > 60
                                    ? 'bg-[#00e87a]/15 text-[#00e87a]'
                                    : 'bg-white/5 text-[#a8b2c1]'
                                }`}
                              >
                                {getHitRate(t)}%
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center text-[#a8b2c1] text-sm">
                              {t.won_picks}/{t.total_picks}
                            </td>
                            <td className="px-4 py-4 text-center text-[#00e87a] font-bold text-sm">
                              {t.ytd_profit || '—'}
                            </td>
                            <td className="px-4 py-4 text-center text-[#a8b2c1] text-sm tabular-nums">
                              {t.avg_odds > 0 ? t.avg_odds.toFixed(2) : '—'}
                            </td>
                            <td className="px-4 py-4 text-center">
                              <span
                                className="px-2 py-0.5 rounded-full text-xs font-bold"
                                style={{
                                  background: `${t.badge_color || '#a8b2c1'}20`,
                                  color: t.badge_color || '#a8b2c1',
                                }}
                              >
                                {t.badge || 'Nuevo'}
                              </span>
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
